import React, { useCallback, useEffect, useRef, useState } from 'react';
// @ts-ignore Legacy provider is JavaScript; product auth remains outside the engine adapter.
import { AuthProvider as LegacyAuthProvider } from '../../AuthProvider';
import EngineDemoUI from '../../demos/EngineDemoUI';
import type { PresentationDraft, SessionPhase } from '../session/PresentationSession';
import EditorOverlay from './EditorOverlay';
import { useStoryModel } from '../story/useStoryModel';
import type { DraftAccess } from './DraftDialog';
import type { DraftRequest, DraftResponse } from '../cloud/PresentationApiClient';

interface EditorWorkspaceProps {
  draft: PresentationDraft;
  sessionPhase: SessionPhase;
  saveRequest: number;
  onContentChange(content: string): void;
  onSave(): Promise<void>;
  onUploadMedia?: (file: File) => Promise<string>;
  onCreateDraft?: (request: DraftRequest) => Promise<DraftResponse>;
  draftAccess?: DraftAccess;
}

export default function EditorWorkspace({
  draft,
  sessionPhase,
  saveRequest,
  onContentChange,
  onSave,
  onUploadMedia,
  onCreateDraft,
  draftAccess,
}: EditorWorkspaceProps) {
  const [initialPresentation] = useState(() => ({
    id: draft.id,
    title: draft.title,
    content: draft.content,
    filename: `${draft.title || 'Untitled'}.md`,
  }));
  const model = useStoryModel(draft.content, onContentChange);
  // Latest markdown for the save callback: a save only confirms the revision it sent (#1758).
  const latestMarkdownRef = useRef(model.markdown);
  latestMarkdownRef.current = model.markdown;
  const [markdownOpen, setMarkdownOpen] = useState(false);
  const [markdownDraft, setMarkdownDraft] = useState(model.markdown);
  const handledSaveRequest = useRef(saveRequest);

  const save = useCallback(async () => {
    const submitted = latestMarkdownRef.current;
    try {
      await onSave();
    } catch {
      return; // the host shows the error; the model stays dirty on purpose
    }
    // Edits made while the request was in flight are not saved yet: stay dirty.
    if (latestMarkdownRef.current === submitted) model.markSaved();
  }, [model, onSave]);

  useEffect(() => {
    if (sessionPhase === 'saved') model.markSaved();
  }, [model.markSaved, sessionPhase]);

  useEffect(() => {
    if (saveRequest === handledSaveRequest.current) return;
    handledSaveRequest.current = saveRequest;
    void save();
  }, [save, saveRequest]);

  const openMarkdown = () => {
    setMarkdownDraft(model.markdown);
    setMarkdownOpen(true);
  };

  const saveState = sessionPhase === 'saving'
    ? 'saving'
    : sessionPhase === 'saveError'
      ? 'saveError'
      : model.dirty || sessionPhase === 'dirty'
        ? 'dirty'
        : sessionPhase === 'saved'
          ? 'saved'
          : 'local';

  return (
    <div className="ps-editor-workspace" data-testid="visual-editor">
      <LegacyAuthProvider>
        <EngineDemoUI
          initialPresentation={initialPresentation}
          livePresentationContent={model.markdown}
          authoringMode
        />
      </LegacyAuthProvider>
      <EditorOverlay
        model={model}
        saveState={saveState}
        onOpenMarkdown={openMarkdown}
        onUploadMedia={onUploadMedia}
        onCreateDraft={onCreateDraft}
        draftAccess={draftAccess}
        onFocusStep={(_id, index) => {
          try {
            (window as Window & { engineApp?: { setActivePOIByIndex?: (value: number) => void } })
              .engineApp?.setActivePOIByIndex?.(index);
          } catch {
            // The engine may still be starting; selection itself remains valid.
          }
        }}
      />

      {markdownOpen && (
        <div className="ps-markdown-backdrop" role="dialog" aria-modal="true" aria-label="Markdown bearbeiten">
          <section className="ps-markdown-dialog">
            <header>
              <div>
                <div className="ps-eyebrow">Expertenmodus</div>
                <h2>Markdown</h2>
              </div>
              <button className="ps-button ps-button-quiet" onClick={() => setMarkdownOpen(false)}>Schließen</button>
            </header>
            <p>Globale Einstellungen und unbekannte Eigenschaften bleiben beim visuellen Bearbeiten erhalten.</p>
            <textarea
              data-testid="raw-markdown-editor"
              value={markdownDraft}
              onChange={(event) => setMarkdownDraft(event.target.value)}
              spellCheck={false}
            />
            <footer>
              <button className="ps-button ps-button-quiet" onClick={() => setMarkdownOpen(false)}>Abbrechen</button>
              <button
                data-testid="apply-markdown"
                className="ps-button ps-button-primary"
                onClick={() => {
                  model.replaceMarkdown(markdownDraft);
                  setMarkdownOpen(false);
                }}
              >Übernehmen</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
